import React from 'react';
import { ImageProps } from '@components/image';
import generateId from '@helpers/generate-id';
import getBlogType from '@helpers/get-blog-type';
import getPrettyTypeName from '@helpers/get-pretty-type-name';
// import TagList from './tag-list/tag-list';
import {
  StyledPost,
  PostInfo,
  PostContent,
  ImageLink,
  StyledImage,
  ViewLink,
  EmptyState,
  PostsGrid,
  ImageContainer,
  StyledText,
  // Meta,
  Category,
  CategoryLink,
} from './posts-list.shared.styled';

const postsListIdGenerator = generateId();

export interface PostsListProps {
  posts: PostPreviewType[];
  blogUrl: string;
  postsType: string;
}

interface PostDataProps {
  category: {
    id: string;
    data: {
      name: string;
    };
  };
  title: {
    text: string;
  }[];
  description: {
    text: string;
  }[];
  featured_image: ImageProps;
}

export interface PostPreviewType {
  data: PostDataProps;
  uid: string;
  first_publication_date: string;
  tags: string[];
  type: 'blog_post' | 'guides_post' | 'case_studies_post';
}

interface PostImageProps {
  post: PostPreviewType;
}

const PostsList = ({ postsType, posts, blogUrl }: PostsListProps): JSX.Element => {
  const currentUrl =
    blogUrl === '/' || blogUrl === '/resources/' ? blogUrl : blogUrl.replace(/\/+$/, '');

  const PostImage = ({ post }: PostImageProps): JSX.Element => {
    const { data, uid, type } = post;
    return data?.featured_image?.url ? (
      <ImageContainer postsType={postsType}>
        <ImageLink
          title={`View this ${getPrettyTypeName(type)} post`}
          href={`${
            currentUrl === '/' || currentUrl === '/resources/' ? `${getBlogType(type)}` : currentUrl
          }/${uid}`}
        >
          <StyledImage
            url={data.featured_image.url}
            alt={data.featured_image.alt}
            gatsbyImageData={data.featured_image.gatsbyImageData}
            objectFit="contain"
          />
        </ImageLink>
      </ImageContainer>
    ) : (
      <></>
    );
  };

  const PostContentBlock = ({ post }: PostImageProps): JSX.Element => {
    const { data, uid, type } = post;
    const imageUrl = post.data.featured_image?.url;
    const isImageless =
      imageUrl == null || (typeof imageUrl === 'string' && imageUrl.trim().length === 0);
    return (
      <PostContent isImageless={isImageless}>
        <PostInfo>
          {data.category?.data?.name && (
            <span>
              <CategoryLink
                href={`${blogUrl}?category=${data.category?.id}`}
                title={`Explore more posts about ${data.category?.data?.name}`}
              >
                <Category>{data.category?.data?.name}</Category>
              </CategoryLink>
            </span>
          )}
          {data.title && data.title[0] && (
            <ViewLink
              href={`${
                currentUrl === '/' || currentUrl === '/blog/' ? getBlogType(type) : currentUrl
              }/${uid}`}
            >
              <StyledText
                type="title"
                tag="h3"
                fontSize="heading6"
                fontColor="secondary"
                stringText={data.title[0].text}
              />
            </ViewLink>
          )}
        </PostInfo>
        {/* <Meta>
          <TagList tags={tags} blogUrl={blogUrl} />
        </Meta> */}
      </PostContent>
    );
  };

  const postList =
    posts.length > 0 ? (
      <PostsGrid>
        {posts.map((post) => {
          const postId = postsListIdGenerator.next().value;

          return (
            <StyledPost key={postId} minHeight={post.data.featured_image?.url ? 'unset' : '14rem'}>
              <PostImage post={post} />
              <PostContentBlock post={post} />
            </StyledPost>
          );
        })}
      </PostsGrid>
    ) : (
      <EmptyState>No posts found!</EmptyState>
    );

  return postList;
};

export default PostsList;
